import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../../api/cabinet";
import { requestPlayTicket, buildPlayerUrl } from "../../../api/playTicket";
import { useAuth } from "../../../context/AuthContext";
import { useCabinet } from "../../../context/CabinetContext";
import { useSanctions } from "../../../context/SanctionsContext";
import MutedNotice from "../../MutedNotice";
import PlayCheckModal from "../PlayCheckModal";
import CabIcon, { PlaceMedal } from "../CabIcon";
import { Button, EmptyState, fmtCompact, fmtNum } from "../../ui";
import {
  genreLabel,
  formatLeaderboardTime,
  formatCommentDate,
  formatDateDMY,
  avatarInitialColor,
} from "../cabinetUtils";
import { profilePhotoSrc } from "../../../api/cabinet";
import "../../../styles/cabinet-game.css";

// Geometry-Dash ID'ы: 295 — лаунчер (карточка в ленте),
// 296-358 — конкретные уровни. Кнопка «ИГРАТЬ» на любом из них шлёт
// юзера в меню эпох GD (/app/gd) — там он выбирает уровень и запускает
// через ticket → плеер. При расширении (Эпоха III и далее) добавлять id.
const GD_LEVEL_IDS = new Set([
  295,
  296, 297, 298, 299, 300, 301, 302, 303, 304, 305,
  306, 350, 351, 352, 353, 354, 355, 356, 357, 358,
]);

/**
 * GameScreen — детальная страница игры. Порт GamePage.kt, 1-в-1.
 * Отличие от apk: кнопка «ИГРАТЬ» не запускает движок (на сайте играть
 * нельзя) — открывает PlayCheckModal: проверка приложения / предложение скачать.
 *
 * props: gameId, onBack
 */
export default function GameScreen({ gameId, onBack }) {
  const { isAuthenticated, user } = useAuth();
  const { selectedSkin } = useCabinet();
  const { isMuted } = useSanctions();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [author, setAuthor] = useState(null); // профиль автора игры (для аватарки)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likeStatus, setLikeStatus] = useState(null);
  /* Флаги «запрос упал» отдельно от «данных нет»: без них пустой список
     и неудачная загрузка выглядят одинаково, и интерфейс делает
     утверждение, которое не проверял. */
  const [commentsFailed, setCommentsFailed] = useState(false);
  const [boardFailed, setBoardFailed] = useState(false);
  const [comments, setComments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showPlayModal, setShowPlayModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState(null);
  // Защита от двойного клика по «ИГРАТЬ» пока летит play-ticket-запрос.
  const [playPending, setPlayPending] = useState(false);

  /* загрузка */
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    api
      /* user_id обязателен: черновик storys отдаёт только владельцу.
         Эффект уже зависит от user?.userId, поэтому после входа
         запрос повторится сам. */
      .getProject(gameId, user?.userId || null, ctrl.signal)
      .then((d) => {
        setDetails(d);
        setLoading(false);
        // Подтягиваем профиль автора чтобы показать аватарку и сделать
        // кликабельный переход на /app/profile/<user_id>.
        if (d && d.user_id) {
          api.getUserById(d.user_id, ctrl.signal)
            .then(setAuthor)
            .catch(() => setAuthor(null));
        }
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(e.message || "Ошибка загрузки");
        setLoading(false);
      });

    if (user?.userId) {
      api
        .getLikeStatus(gameId, user.userId, ctrl.signal)
        .then(setLikeStatus)
        .catch(() => {});
    }
    /* Комментарии и лидерборд помечаем как «не загрузились», а не просто
       глотаем ошибку. Раньше упавший запрос комментариев показывал
       «Пока нет комментариев. Будь первым!» — прямая ложь: комментарии
       есть, их не удалось получить. То же с лидербордом: пустой блок
       читался как «рекордов нет». */
    api
      .getComments(gameId, ctrl.signal)
      .then((list) => {
        setComments(list);
        setCommentsFailed(false);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setCommentsFailed(true);
      });
    api
      .getLeaderboard(gameId, 10, ctrl.signal)
      .then((list) => {
        setLeaderboard(list);
        setBoardFailed(false);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setBoardFailed(true);
      });

    return () => ctrl.abort();
    /* Зависимость по user?.userId, а НЕ по объекту user: контекст
       пересоздаёт объект при каждом обновлении (баланс, профиль), и с
       `user` в deps вся страница уходила в перезагрузку — сбрасывались
       комментарии, лидерборд и уже загруженные детали. Идентификатор
       примитивен и меняется только при реальной смене пользователя. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, user?.userId]);

  /* Лайк / дизлайк.
     Проверено на боевом API: /like-status и /like возвращают ТОЛЬКО
     {liked, vote} — счётчиков в ответе нет. Раньше компонент ждал в ответе
     likes_count, не получал его и продолжал показывать число из details,
     то есть счётчик НЕ менялся после нажатия: человек лайкал, а «12»
     оставалось «12» до перезагрузки страницы.
     Поэтому считаем дельту сами и держим счётчики в своём состоянии. */
  const [voteCounts, setVoteCounts] = useState(null);
  const [voteBusy, setVoteBusy] = useState(false);
  const [voteError, setVoteError] = useState(null);

  const handleVote = useCallback(
    async (target) => {
      if (!user?.userId || voteBusy) return;   // защита от гонки быстрых кликов
      const prevVote = likeStatus?.vote === "like" || likeStatus?.liked
        ? "like"
        : likeStatus?.vote === "dislike"
        ? "dislike"
        : "none";
      const newKind = prevVote === target ? "none" : target;

      setVoteBusy(true);
      setVoteError(null);
      try {
        const res = await api.toggleLike(gameId, user.userId, newKind);
        setLikeStatus(res);
        setVoteCounts((prev) => {
          const base = prev || {
            likes: details?.likes_count || 0,
            dislikes: details?.dislikes_count || 0,
          };
          const delta = (from, to, kind) =>
            (to === kind ? 1 : 0) - (from === kind ? 1 : 0);
          return {
            likes: Math.max(0, base.likes + delta(prevVote, newKind, "like")),
            dislikes: Math.max(
              0,
              base.dislikes + delta(prevVote, newKind, "dislike")
            ),
          };
        });
      } catch {
        // Молчаливый провал делал кнопку «сломанной»: ничего не менялось
        // и человек жал снова. Теперь причина видна.
        setVoteError("Не получилось. Попробуй ещё раз.");
      } finally {
        setVoteBusy(false);
      }
    },
    [gameId, user?.userId, likeStatus, voteBusy, details]
  );

  /* запуск игры */
  // Логика:
  //   - не залогинен          → старый PlayCheckModal (предлагает войти/скачать APK)
  //   - залогинен + ticket OK → редирект в плеер
  //   - залогинен + ticket FAIL → старый PlayCheckModal как fallback
  //
  // GD-уровни (295 лаунчер, 296-358) запускаются ТАК ЖЕ как обычные
  // игры — через ticket → player.rublox.pro/<id>. Плеер сам распознаёт
  // GD по id-диапазону (BabylonScene.js isGd) и подгружает скин/трейл/
  // прогресс из gd_progress.
  //
  // Раньше для GD здесь был редирект на minecraftia-school.ru/kubikon/gd
  // (меню эпох). НО у юзера rublox.pro нет сессии на mnk — разные
  // домены, разные localStorage — получал 401. Поэтому GD-special-case
  // убран. Меню эпох GD появится прямо в rublox-site когда портируем.
  //
  // Fallback важен: если бэк-эндпоинт временно недоступен, юзер не
  // должен оставаться без альтернативы.
  const handlePlay = useCallback(async () => {
    if (playPending) return;
    if (!isAuthenticated) {
      setShowPlayModal(true);
      return;
    }
    // GD-карточки (295 лаунчер, 296-358 уровни) → меню эпох внутри
    // rublox.pro. Юзер выберет эпоху и конкретный уровень — оттуда
    // запуск через ticket → плеер.
    if (GD_LEVEL_IDS.has(Number(gameId))) {
      navigate("/app/gd");
      return;
    }
    setPlayPending(true);
    try {
      const ticket = await requestPlayTicket();
      const url = buildPlayerUrl(gameId, ticket, selectedSkin);
      // URL печатать в консоль нельзя: в нём одноразовый play-ticket,
      // дающий доступ к сессии. В браузере ребёнка консоль открыта
      // не реже, чем у взрослого.
      // Чистый redirect: страница ушла, состояние setPlayPending(false) уже
      // не успеет отработать — не страшно.
      window.location.assign(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[playTicket] failed, fallback to PlayCheckModal:", e?.message || e);
      setPlayPending(false);
      setShowPlayModal(true);
    }
  }, [playPending, isAuthenticated, gameId, navigate, selectedSkin]);

  /* Отправка комментария.
     Два исправления против прежней версии:
     1. Текст очищается ТОЛЬКО после успешной отправки. Раньше поле
        чистилось сразу, и если перезагрузка списка падала — человек
        оставался без своего текста и без комментария на экране, то есть
        выглядело как «пропало».
     2. Есть sending: кнопка без индикатора допускала двойной клик,
        и комментарий уходил дважды. */
  const [sending, setSending] = useState(false);
  const handlePostComment = useCallback(async () => {
    const text = commentText.trim();
    if (text.length < 2 || !user?.userId || sending) return;
    setCommentError(null);
    setSending(true);
    try {
      await api.postComment(gameId, user.userId, text);
      // Отправка удалась — теперь очищать поле безопасно.
      setCommentText("");
      try {
        const updated = await api.getComments(gameId);
        setComments(updated);
        setCommentsFailed(false);
      } catch {
        // Комментарий отправлен, но список не обновился — это не ошибка
        // отправки. Добавляем свой комментарий локально, чтобы человек
        // видел результат своего действия.
        setComments((prev) => [
          ...(Array.isArray(prev) ? prev : []),
          {
            id: `local-${Date.now()}`,
            text,
            user_id: user.userId,
            username: user.username,
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } catch (e) {
      // ApiError.message уже переведён в client.js (см. ERROR_MESSAGES).
      setCommentError(e?.message || "Не удалось отправить комментарий.");
    } finally {
      setSending(false);
    }
  }, [commentText, gameId, user, sending]);

  /* Сообщение об ошибке гасим по таймеру, но с очисткой: setTimeout без
     cleanup срабатывал уже после ухода со страницы. */
  useEffect(() => {
    if (!commentError) return undefined;
    const t = setTimeout(() => setCommentError(null), 7000);
    return () => clearTimeout(t);
  }, [commentError]);

  return (
    <div className="rb-game">
      {/* app-bar */}
      <div className="rb-game__appbar">
        <button className="rb-game__back" onClick={onBack} aria-label="Назад">
          <CabIcon name="back" size={20} />
        </button>
        <span className="rb-game__appbar-title">
          {details?.title || "Игра"}
        </span>
      </div>

      {loading ? (
        <div className="rb-game__center">
          <div className="rb-cab-spinner" />
        </div>
      ) : error ? (
        <div className="rb-game__center">
          <div className="rb-cab-empty__text">
            Не удалось загрузить: {error}
          </div>
        </div>
      ) : details ? (
        <GameContent
          d={details}
          author={author}
          navigate={navigate}
          likeStatus={likeStatus}
          voteCounts={voteCounts}
          voteBusy={voteBusy}
          voteError={voteError}
          comments={comments}
          leaderboard={leaderboard}
          commentText={commentText}
          setCommentText={setCommentText}
          canComment={isAuthenticated && !isMuted}
          canVote={isAuthenticated}
          sending={sending}
          commentsFailed={commentsFailed}
          boardFailed={boardFailed}
          muted={isMuted}
          onVote={handleVote}
          onPostComment={handlePostComment}
          onPlay={handlePlay}
          playPending={playPending}
          commentError={commentError}
        />
      ) : null}

      {showPlayModal && (
        <PlayCheckModal
          game={details}
          onClose={() => setShowPlayModal(false)}
        />
      )}
    </div>
  );
}

/* ── контент страницы игры ─────────────────────────────────────── */
function GameContent({
  d,
  author,
  navigate,
  likeStatus,
  comments,
  leaderboard,
  commentText,
  setCommentText,
  canComment,
  /* Голосовать может любой вошедший: мут выдаётся за текст, и лишать
     за него возможности лайкнуть — наказание не по делу. */
  canVote,
  muted,
  onVote,
  onPostComment,
  onPlay,
  playPending,
  commentError,
  voteCounts,
  voteBusy,
  voteError,
  sending,
  commentsFailed,
  boardFailed,
}) {
  /* Счётчики: API их не отдаёт (проверено — /like возвращает только
     {liked, vote}), поэтому после голоса берём пересчитанные локально,
     а до первого голоса — из details. */
  const likes = voteCounts ? voteCounts.likes : d.likes_count || 0;
  const dislikes = voteCounts ? voteCounts.dislikes : d.dislikes_count || 0;
  const isLiked = likeStatus?.vote === "like" || likeStatus?.liked;
  const isDisliked = likeStatus?.vote === "dislike";

  const chips = [];
  if (d.multiplayer)
    chips.push({ iconName: "globe", text: "Онлайн", cls: "online" });
  if ((d.rating_percent || 0) >= 90)
    chips.push({ iconName: "fire", text: "Хит", cls: "hit" });
  if ((d.max_players || 0) > 1)
    chips.push({ iconName: "users", text: `до ${d.max_players}`, cls: "players" });

  return (
    <div className="rb-game__scroll">
      <div className="rb-game__layout">
        {/* ── ЛЕВАЯ КОЛОНКА: превью + действия (sticky на десктопе) ── */}
        <div className="rb-game__left">
          {/* превью игры — НЕ растягиваем на всю ширину, держим в карточке */}
          <div className="rb-game__preview">
            {d.thumbnail ? (
              <img src={d.thumbnail} alt={d.title} />
            ) : (
              <div className="rb-game__preview-empty">
                {(d.title || "?").charAt(0).toUpperCase()}
              </div>
            )}
            {/* Возраст показываем ТОЛЬКО когда он задан. Раньше `|| 0`
                превращало отсутствие рейтинга в «0+», то есть в утверждение
                «подходит всем» — для площадки с детской аудиторией это
                неверное утверждение, а не просто неточность. */}
            {d.age_rating > 0 && (
              <span className="rb-game__age">{d.age_rating}+</span>
            )}
          </div>

          {/* кнопка ИГРАТЬ.
              Подпись при запуске НЕ убираем. Пустая кнопка с одним кольцом
              не объясняет, что происходит, а ширина всё равно не меняется —
              она задана в 100%. Поэтому меняем только слово, а кольцо
              встаёт на место иконки: положение подписи не скачет. */}
          <button
            className={`rb-game__play ${playPending ? "is-loading" : ""}`}
            onClick={onPlay}
            disabled={playPending}
            aria-busy={playPending || undefined}
          >
            {playPending ? (
              <>
                <span className="rb-game__play-spinner" aria-hidden="true" />
                ЗАПУСКАЕМ
              </>
            ) : (
              <>
                <CabIcon name="play" size={20} />
                ИГРАТЬ
              </>
            )}
          </button>

          {/* Лайк / дизлайк.
              disabled по canVote, а НЕ по canComment: canComment включает
              мут за комментарии, и человек с мутом терял возможность
              лайкать — оценка это не текст, за неё не банят.
              Счётчики через fmtCompact («12 тыс.»), а не formatCompact
              («12K»): английские суффиксы остались от Android-порта. */}
          <div className="rb-game__votes">
            <button
              className={`rb-game__vote ${
                isLiked ? "is-active rb-game__vote--like" : ""
              }`}
              onClick={() => onVote("like")}
              disabled={!canVote || voteBusy}
              aria-pressed={isLiked}
            >
              <CabIcon name="heart" size={17} />
              <span className="ui-num">{fmtCompact(likes)}</span>
            </button>
            <button
              className={`rb-game__vote ${
                isDisliked ? "is-active rb-game__vote--dislike" : ""
              }`}
              onClick={() => onVote("dislike")}
              disabled={!canVote || voteBusy}
              aria-pressed={isDisliked}
            >
              <CabIcon name="heartBroken" size={17} />
              <span className="ui-num">{fmtCompact(dislikes)}</span>
            </button>
          </div>
          {voteError && <div className="rb-game__vote-error">{voteError}</div>}

          {/* метрики */}
          <div className="rb-game__metrics">
            <div className="rb-game__metric">
              <div className="rb-game__metric-val rb-game__metric-val--blue">
                <CabIcon name="eye" size={17} />
                {fmtCompact(d.play_count)}
              </div>
              <div className="rb-game__metric-label">Запусков</div>
            </div>
            <div className="rb-game__metric-divider" />
            <div className="rb-game__metric">
              <div className="rb-game__metric-val rb-game__metric-val--yellow">
                <CabIcon name="gamepad" size={17} />
                {d.rating_percent || 0}%
              </div>
              <div className="rb-game__metric-label">Рейтинг</div>
            </div>
          </div>
        </div>

        {/* ── ПРАВАЯ КОЛОНКА: инфо о игре ─────────────────────────── */}
        <div className="rb-game__right">
          {/* заголовок + автор */}
          <h1 className="rb-game__title">{d.title}</h1>
          <div className="rb-game__author">
            <AuthorChip
              userId={d.user_id}
              username={d.author_username || author?.firstName}
              photo={author?.photo}
              onClick={() => d.user_id && navigate(`/app/profile/${d.user_id}`)}
            />
            {d.genre && (
              <>
                <span className="rb-game__dot">·</span>
                <span className="rb-game__genre">{genreLabel(d.genre)}</span>
              </>
            )}
          </div>

          {/* чипы */}
          {chips.length > 0 && (
            <div className="rb-game__chips">
              {chips.map((c) => (
                <span
                  key={c.cls}
                  className={`rb-game__chip rb-game__chip--${c.cls}`}
                >
                  <CabIcon name={c.iconName} size={13} />
                  {c.text}
                </span>
              ))}
            </div>
          )}

          {/* описание */}
          {d.description && (
            <>
              <h2 className="rb-game__sec-title">Описание</h2>
              <div className="rb-game__desc">{d.description}</div>
            </>
          )}

          {/* даты создания и обновления игры */}
          {(d.created_at || d.updated_at) && (
            <div className="rb-game__dates">
              {d.created_at && (
                <div className="rb-game__date">
                  <CabIcon name="calendar" size={14} />
                  <span className="rb-game__date-label">Создана:</span>
                  <span className="rb-game__date-val">{formatDateDMY(d.created_at)}</span>
                </div>
              )}
              {d.updated_at && (
                <div className="rb-game__date">
                  <CabIcon name="refresh" size={14} />
                  <span className="rb-game__date-label">Обновлена:</span>
                  <span className="rb-game__date-val">{formatDateDMY(d.updated_at)}</span>
                </div>
              )}
            </div>
          )}

          {/* лидерборд */}
          {leaderboard.length > 0 && (
            <>
              <h2 className="rb-game__sec-title">
                <CabIcon name="trophy" size={17} /> Лидерборд{" "}
                <span className="rb-game__sec-sub">
                  Топ-{leaderboard.length}
                </span>
              </h2>
              <div className="rb-game__lb">
                {leaderboard.map((rec, i) => (
                  <div key={i} className="rb-game__lb-row">
                    <span className="rb-game__lb-place">
                      <PlaceMedal place={i + 1} size={18} />
                    </span>
                    <span className="rb-game__lb-name">
                      {rec.username || `Игрок #${rec.user_id}`}
                    </span>
                    <span
                      className={`rb-game__lb-time ${
                        i === 0 ? "rb-game__lb-time--gold" : ""
                      }`}
                    >
                      {formatLeaderboardTime(rec.time_ms)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* комментарии */}
          <h2 className="rb-game__sec-title">
            Комментарии{" "}
            <span className="rb-game__sec-sub ui-num">{fmtNum(comments.length)}</span>
          </h2>
          <div className="rb-game__comment-input">
            {muted && (
              <div style={{ marginBottom: 10 }}>
                <MutedNotice action="comment" />
              </div>
            )}
            {!muted && (
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={
                canComment
                  ? "Напиши комментарий..."
                  : "Войдите, чтобы комментировать"
              }
              disabled={!canComment}
              rows={3}
            />)}
            {/* Ошибка была свёрстана инлайн-стилями с цветом #ff4060,
                которого нет ни в палитре, ни где-либо ещё в проекте.
                Теперь семантические токены — цвет живёт вместе с темой. */}
            {commentError && (
              <div className="rb-game__comment-error">{commentError}</div>
            )}
            <div className="rb-game__comment-actions">
              <Button
                variant="primary"
                onClick={onPostComment}
                loading={sending}
                disabled={!canComment || commentText.trim().length < 2}
              >
                Отправить
              </Button>
            </div>
          </div>

          {commentsFailed ? (
            /* Раньше упавший запрос показывал «Пока нет комментариев» —
               интерфейс утверждал то, чего не проверял. */
            <EmptyState
              icon={<CabIcon name="warning" size={36} />}
              title="Комментарии не загрузились"
              desc="Обнови страницу, чтобы попробовать снова."
            />
          ) : comments.length === 0 ? (
            <EmptyState
              icon={<CabIcon name="chat" size={36} />}
              title="Пока нет комментариев"
              desc={canComment ? "Будь первым — напиши, как тебе игра." : "Войди, чтобы оставить первый."}
            />
          ) : (
            <div className="rb-game__comments">
              {comments.map((c) => (
                <CommentItem key={c.id} comment={c} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── комментарий ───────────────────────────────────────────────── */
function CommentItem({ comment }) {
  const name =
    comment.displayName ||
    comment.username ||
    (comment.user_id > 0 ? `Игрок #${comment.user_id}` : "?");
  const avatar = api.avatarSrc(comment.photo_thumb_b64);
  return (
    <div className="rb-game__comment">
      <div className="rb-game__comment-head">
        {avatar ? (
          <img className="rb-game__comment-avatar" src={avatar} alt={name} />
        ) : (
          <span
            className="rb-game__comment-avatar"
            style={{ background: avatarInitialColor(name) }}
          >
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="rb-game__comment-name">{name}</span>
        <span className="rb-game__comment-date">
          {formatCommentDate(comment.created_at)}
        </span>
      </div>
      <div className="rb-game__comment-text">{comment.text}</div>
    </div>
  );
}

/**
 * AuthorChip — кликабельный блок с аватаркой автора игры рядом с заголовком.
 * Используется в GameScreen. Клик → переход на /app/profile/<user_id>.
 *
 * photo — относительный путь к файлу (assets/profile/N.png) который мы
 * пропускаем через profilePhotoSrc. Если photo пусто или картинка не
 * загрузилась — показываем цветной круг с инициалом, как в других
 * местах кабинета (см. avatarInitialColor).
 */
function AuthorChip({ userId, username, photo, onClick }) {
  const name = username || "Автор";
  const initial = String(name).charAt(0).toUpperCase();
  const photoUrl = photo ? profilePhotoSrc(photo) : null;
  const clickable = Boolean(userId);

  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className="rb-game__author-chip"
      title={clickable ? "Открыть профиль автора" : ""}
    >
      <span className="rb-game__author-avatar">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{ background: avatarInitialColor(name) }}>{initial}</span>
        )}
      </span>
      <span className="rb-game__author-name">{name}</span>
    </button>
  );
}
